import React, {
  ComponentType,
  forwardRef,
  Fragment,
  MouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import cn from 'clsx';
import { useToggle, useUpdateEffect } from 'react-use';
import { defer } from 'lodash-es';

import { Button } from '../Button';
import { Icons } from '../Icons';

import styles from './Collapse.module.scss';

export interface CollapseProps {
  icon?: ComponentType;
  title: React.ReactNode;
  initialOpen?: boolean;
  children: ReactNode;
  className?: string;
  onCollapse?: () => void;
  onExpand?: () => void;
}
export interface CollapseRef {
  toggle: () => void;
}

export const Collapse = forwardRef(
  (
    {
      title,
      children,
      initialOpen = false,
      icon: Icon = Fragment,
      className,
      onCollapse,
      onExpand
    }: CollapseProps,
    ref: Ref<CollapseRef>
  ) => {
    const [isOpen, toggleIsOpen] = useToggle(initialOpen);

    useImperativeHandle(ref, () => ({
      toggle: toggleIsOpen
    }));

    const content = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
      const { current: container } = content;
      if (!container) return;
      if (isOpen) {
        container.style.maxHeight = '';
        const { scrollHeight } = container;
        container.style.maxHeight = '0';
        const runner = window.requestAnimationFrame ?? defer;
        runner(() => {
          container.style.maxHeight = `${scrollHeight}px`;
        });
      } else {
        container.style.maxHeight = '0';
      }
    }, [children, isOpen]);

    const toggle = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        toggleIsOpen();
      },
      [toggleIsOpen]
    );

    useUpdateEffect(() => {
      const handler = isOpen ? onExpand : onCollapse;
      handler?.();
    }, [isOpen, onCollapse, onExpand]);

    return (
      <div
        className={cn(styles.collapse, className, { [styles.open]: isOpen })}
      >
        <div className={styles.header} onClick={toggle} role="none">
          <Icon />
          {title}
          {children ? (
            <Button
              variant="phantom"
              className={cn(styles.trigger, { [styles.rotate]: isOpen })}
            >
              <Icons.ArrowDrop />
            </Button>
          ) : null}
        </div>
        <div
          ref={content}
          style={{ maxHeight: '0' }}
          className={cn(styles.collapseContainer)}
        >
          <div className={cn(styles.collapseBody)}>{children}</div>
        </div>
      </div>
    );
  }
);
